
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface BillingAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: 'supabase' | 'internal';
}

const BillingAddressModal: React.FC<BillingAddressModalProps> = ({ 
  isOpen, 
  onClose, 
  userType 
}) => {
  const [billingAddress, setBillingAddress] = useState({
    firstName: 'John',
    lastName: 'Doe',
    company: '',
    address1: '123 Main St',
    address2: '',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'US'
  });

  const handleSave = () => {
    if (!billingAddress.firstName || !billingAddress.lastName || !billingAddress.address1 || 
        !billingAddress.city || !billingAddress.state || !billingAddress.zipCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Here you would typically save to your backend
    toast.success('Billing address updated successfully');
    onClose();
  };

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Update Billing Address</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="text-gray-300">First Name *</Label>
              <Input
                id="firstName"
                value={billingAddress.firstName}
                onChange={(e) => setBillingAddress({...billingAddress, firstName: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="lastName" className="text-gray-300">Last Name *</Label>
              <Input
                id="lastName"
                value={billingAddress.lastName}
                onChange={(e) => setBillingAddress({...billingAddress, lastName: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="company" className="text-gray-300">Company (Optional)</Label>
            <Input
              id="company"
              value={billingAddress.company}
              onChange={(e) => setBillingAddress({...billingAddress, company: e.target.value})}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label htmlFor="address1" className="text-gray-300">Address Line 1 *</Label>
            <Input
              id="address1"
              value={billingAddress.address1}
              onChange={(e) => setBillingAddress({...billingAddress, address1: e.target.value})}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div>
            <Label htmlFor="address2" className="text-gray-300">Address Line 2 (Optional)</Label>
            <Input
              id="address2"
              value={billingAddress.address2}
              onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="text-gray-300">City *</Label>
              <Input
                id="city"
                value={billingAddress.city}
                onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="state" className="text-gray-300">State/Province *</Label>
              <Input
                id="state"
                value={billingAddress.state}
                onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="zipCode" className="text-gray-300">ZIP/Postal Code *</Label>
              <Input
                id="zipCode"
                value={billingAddress.zipCode}
                onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="country" className="text-gray-300">Country *</Label>
              <Select
                value={billingAddress.country}
                onValueChange={(value) => setBillingAddress({...billingAddress, country: value})}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {countries.map((country) => (
                    <SelectItem key={country.value} value={country.value} className="text-white">
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              className="bg-sports-green text-black hover:bg-green-600"
            >
              Save Address
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            {userType === 'supabase' ? 'Old School account billing address' : 'New School account billing address'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BillingAddressModal;
